import React, { useCallback, useEffect, useRef, useState } from 'react';
import Form from 'devextreme-react/form';
import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';
import { Col, Row } from 'react-bootstrap';
import { roleService } from '@/projects/admin/services/role-service';
import { applicationService } from '@/projects/admin/services/application-service';
import { accountService } from '@/projects/admin/services/account-service';
import { useNavigate, useParams } from 'react-router-dom';

import { IRole } from '@/core/interfaces/admin/usestate-interfaces';
import { t } from 'i18next';
import DataGrid, { Column, Paging, Editing, Grouping, Selection } from 'devextreme-react/data-grid';
import {
    TreeList, Editing as TlEditing, Column as TlColumn, Button,
} from 'devextreme-react/tree-list';
import { List, Button as DXButton, Popup, TextBox, CheckBox } from 'devextreme-react';
import { ELEMENT_ID } from '@/core/constants/common/element-constants';
import clsx from 'clsx';
import { Required } from '@/core/helper/validation-rule-helper';
import { PARENT_PARAMETERS_CONSTANTS } from '@/core/constants/parameters/parent-parameters-constant';

import FormWrapper from '@/core/wrappers/form-wrapper';
import CustomStore from 'devextreme/data/custom_store';
import { parameterService } from '@/projects/platform/services/parameter-service';
import { createCustomStore } from '@/core/wrappers/custom-store-wrapper';


function sendCheckRoleRequest(email: string) {
    return new Promise(async (resolve) => {
        resolve(true);
    });
}

const asyncRoleValidation = (params: { value: any; }) => sendCheckRoleRequest(params.value);

function RoleDefinition() {
    const formRef: any = useRef<Form>();
    const submitFormRef: any = useRef<HTMLFormElement>();
    const [role, setRole] = useState<IRole>({ id: '', name: '', description: '', applicationId: null, isRestricted: false, accountId: null, parameterAccountTypeId: null, permissions: [], menus: [] });
    const [rolePermissions, setRolePermissions] = useState<any[]>([]);
    const [accountTypes, setAccountTypes] = useState<CustomStore | null>(null);
    const [tab, setTab] = useState('Main')
    const [applications, setApplications] = useState<CustomStore>();
    const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
    const [modules, setModules] = useState<any>([]);
    const [permissions, setPermissions] = useState<Map<string, any>>(new Map());
    const [selected, setSelected] = useState<any>({});
    const [moduleExpanded, setModuleExpanded] = useState(true);
    const [featureExpanded, setFeatureExpanded] = useState(true);
    const params = useParams();
    const roleId = params.id;
    const navigate = useNavigate();

    const actionOptions = [
        { id: "Create", name: t("create") },
        { id: "Update", name: t("update") },
        { id: "Delete", name: t("delete") },
        { id: "Read", name: t("read") },
        { id: "List", name: t("list") }]

    useEffect(() => {
        if (roleId && role.id == '') {
            (async function () {
                const getRole = await roleService.getRole(roleId)
                const data = getRole.data;
                setRole({ id: data.id, name: data.name, description: data.description, applicationId: data.applicationId, isRestricted: data.isRestricted, accountId: data.accountId, parameterAccountTypeId: data.parameterAccountTypeId, permissions: data.permissions, menus: data.menus });
                await fetchFeatures(data.applicationId, data.permissions);
                setSelectedAppId(data.applicationId);
            })();
        }


        setApplications(createCustomStore({
            key: 'id',
            cacheRawData: true,
            load: applicationService.lookupApplication,
            byKey: (key, loadOptions) => {
                if (key) {
                    loadOptions.filter = ["id", "=", key];
                }
            },
        }));

    }, []);

    const [moduleExpandedStates, setModuleExpandedStates] = useState<{ [id: string]: boolean }>({});
    const [featureExpandedStates, setFeatureExpandedStates] = useState<{ [id: string]: boolean }>({});

    const toggleModule = (id: string) => {
        setModuleExpandedStates(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const toggleFeature = (id: string) => {
        setFeatureExpandedStates(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const accounts = createCustomStore({
        key: 'id',
        load: accountService.lookupAccounts,
        byKey: (key, loadOptions) => {
            if (key) {
                loadOptions.filter = ["Id", "=", key];
            }
        },
    });

    useEffect(() => {
        fetchAccountType(selectedAppId);
    }, [selectedAppId]);

    const fetchAccountType = (selectedAppId: any) => {
        const accountTypeStore = new CustomStore({
            key: 'id',
            load: parameterService.getParameters,
            onLoading(loadOptions) {
                loadOptions.filter = [
                    ["ParentCode", "=", PARENT_PARAMETERS_CONSTANTS.ACCOUNT_TYPE],
                    "and",
                    ["ParentId", "<>", null],
                    "and",
                    ["ApplicationId", "=", selectedAppId],
                ];
            },
            byKey: async (key, loadOptions: any) => {
                let returnObject = null;
                if (key) {
                    loadOptions.filter = ["id", "=", key];
                    const objectsByID = (await parameterService.getParameters(loadOptions))
                    returnObject = objectsByID.data.length > 0 ? objectsByID.data[0] : null;
                }
                return returnObject
            },
            useDefaultSearch: true
        });

        setAccountTypes(accountTypeStore);
    };


    const fetchFeatures = async (selectedAppId: any, rolePermissionsParam?: any[]) => {

        var filter: any = [];
        if (selectedAppId) {
            filter = [["ApplicationId", "=", null], "or", ["ApplicationId", "=", selectedAppId]]
        }

        var features = await applicationService.getApplicationFeatures({ filter: filter });

        const permInFeatures = new Map<string, any>();;

        const groupedModules = Object.values(features.data.reduce((acc: any, item: any) => {
            if (!acc[item.applicationModule]) {
                acc[item.applicationModule] = {
                    id: item.applicationModuleId,
                    text: item.applicationModule,
                    features: []
                };
            }
            const feature = {
                id: item.id,
                text: item.name,
                permissions: item.permissionDependencies.map((x: any) => ({
                    id: x.permissionId,
                    text: x.permissionName,
                    type: x.valueType,
                    dependencyType: x.dependencyType,
                    value: JSON.parse(x.permissionValue).value
                }))
            }
            acc[item.applicationModule].features.push(feature);

            feature.permissions.forEach((perm: any) => {
                permInFeatures.set(`${feature.id}::${perm.id}::${perm.dependencyType}::${perm.type}`, perm.value || '');
            })


            return acc;
        }, {}));

        setPermissions(permInFeatures);

        setModules(groupedModules)

        features = features.data.flatMap((feature: any) =>
            feature.permissionDependencies.map((perm: any) => ({
                ...feature,
                ...perm,
                actions: JSON.parse(perm.permissionValue).value
            }))
        );




        const permsToRestore = rolePermissionsParam ?? role.permissions;
        permsToRestore.forEach((perm: any) => {
            ["Required", "Supporting", "Optional"].forEach((item) => {
                var key = `${perm.applicationFeatureId}::${perm.id}::${item}::${perm.valueType}`
                if (permInFeatures.get(key) !== undefined)
                    updateValue(perm.applicationFeatureId, perm.id, item, perm.valueType, JSON.parse(perm.permissionValue).value)
            })

            //togglePermission(perm.applicationFeatureId, perm.id, perm.dependencyType,perm.valueType,true,JSON.parse(perm.permissionValue).value)
        })
    };



    const handleSubmit = useCallback(async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                setTab('Main')
                const roleForm = { ...formRef.current.props.formData };

                const perms = Object.entries(selected).filter(([_, v]) => v !== undefined).map(([key, value]) => {
                    const [featureId, permissionId, dependencyType, valueType] = key.split('::');
                    return { applicationFeatureId: featureId, id: permissionId, permissionValue: { value: value }, valueType };
                });


                const mergedMap = new Map();

                perms.forEach((item: any) => {
                    const key = `${item.applicationFeatureId}::${item.id}::${item.valueType}`;
                    const isAction = item.valueType === "Actions";

                    if (!mergedMap.has(key)) {
                        mergedMap.set(key, {
                            ...item,
                            permissionValue: isAction
                                ? { value: [...item.permissionValue.value] }
                                : { value: item.permissionValue.value }
                        });
                    } else {
                        const existing = mergedMap.get(key);

                        if (isAction) {
                            const combined = new Set([
                                ...existing.permissionValue.value,
                                ...item.permissionValue.value
                            ]);
                            existing.permissionValue.value = Array.from(combined);
                        } else {
                            existing.permissionValue.value = item.permissionValue.value;
                        }
                    }
                });


                roleForm.permissions = Array.from(mergedMap.values()).map(item => ({
                    ...item,
                    permissionValue: JSON.stringify(item.permissionValue)
                }))

                if (roleId) {
                    await roleService.updateRole(null, roleForm)
                } else {
                    await roleService.createRole(roleForm);
                    navigate(`/admin/role-list/`, {});
                }
                resolve();
            } catch (error) {
                reject(error);
            }

        })
    }, [rolePermissions, selected]);


    const nameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'name' },
        disabled: roleId ? true : false
    };

    const descriptionEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'description' },
    };

    const isRestrictedEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'isRestriction' },
        hint: t("admin_only_editable")
    };

    const lastSelectedId = useRef<string | null>(null);
    const applicationEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'application' },
        dataSource: applications,
        displayExpr: 'name',
        valueExpr: 'id',
        placeholder: undefined,
        deferRendering: true,
        onSelectionChanged: (e: any) => {
            const currentId = e.selectedItem?.id ?? null;
            if (currentId === lastSelectedId.current) return;
            lastSelectedId.current = currentId;

            if (e.selectedItem) {
                fetchFeatures(e.selectedItem.id);
                setSelectedAppId(e.selectedItem.id);
            }
        }
    };

    const accountEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'account' },
        dataSource: accounts,
        displayExpr: 'name',
        valueExpr: 'id',
        placeholder: undefined,
        showClearButton: true
    };


    const accountTypeEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'accountType' },
        dataSource: accountTypes,
        displayExpr: 'code',
        valueExpr: 'id',
        placeholder: undefined,
        showClearButton: true
    };

    const getPermissionKeys = (module: any) => {
        return module.features.flatMap((feature: any) =>
            feature.permissions.map((p: any) => `${feature.id}::${p.id}::${p.dependencyType}::${p.type}`)
        );
    };

    const toggleGroup = (keys: any[], checked: any, defaultValues: any) => {
        const updates: any = {};
        keys.forEach((key: string, idx: number) => {
            updates[key] = checked ? defaultValues[idx] : undefined;
        });
        setSelected((prev: any) => ({ ...prev, ...updates }));
    };

    const togglePermission = (fid: any, pid: any, dependencyType: any, type: any, checked: any, defaultVal: any) => {
        const key = `${fid}::${pid}::${dependencyType}::${type}`;
        setSelected((prev: any) => ({
            ...prev,
            [key]: checked ? defaultVal : undefined
        }));
    };

    const updateValue = (fid: any, pid: any, dependencyType: any, type: any, newValue: any) => {

        const key = `${fid}::${pid}::${dependencyType}::${type}`;
        setSelected((prev: any) => ({ ...prev, [key]: newValue }));
    };



    const navigateBack = async () => {
        navigate('/admin/role-list', {})
    }
    return (
        <>
            <div className='card-body col-xl-12'>
                <div className='d-flex justify-content-end'>
                    <div className='col-auto me-5'>
                        <DXButton
                            text={t('back')}
                            type='normal'
                            icon='back'
                            useSubmitBehavior={false}
                            onClick={navigateBack}
                        />
                    </div>
                    <div className='col-auto'>
                        <DXButton
                            text={t("save")}
                            type='default'
                            width={120}
                            onClick={() => {
                                if (formRef.current) {
                                    const validationResult = formRef.current.instance.validate();
                                    if (validationResult.isValid) {
                                        submitFormRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className='card card-custom m-5'>
                <div className='card-header card-header-stretch overflow-auto'>
                    <ul
                        className='nav nav-stretch nav-line-tabs fw-semibold border-transparent flex-nowrap'
                        role='tablist'
                    >
                        <li className='nav-item'>
                            <a
                                className={clsx(`nav-link cursor-pointer`, { active: tab === 'Main' })}
                                onClick={() => setTab('Main')}
                                role='tab'
                            >
                                {t("definition")}
                            </a>
                        </li>
                        <li className='nav-item'>
                            <a
                                className={clsx(`nav-link cursor-pointer`, { active: tab === 'Feature' })}
                                onClick={() => {
                                    if (formRef.current) {
                                        const validationResult = formRef.current.instance.validate();
                                        if (validationResult.isValid) {
                                            rolePermissions && setTab('Feature');
                                        }
                                    }
                                }}
                                role='tab'
                            >
                                {t("permissions")}
                            </a>
                        </li>
                    </ul>
                </div>

                <div className='card-body'>
                    <div className='tab-content pt-3'>

                        <div className={clsx('tab-pane', { active: tab === 'Main' })}>
                            <FormWrapper
                                onSubmit={handleSubmit}
                                formRef={formRef}
                                submitFormRef={submitFormRef}
                                formData={role}
                                isAddBackBtn={false}
                                simpleItemGroups={[
                                    {
                                        useColCountByScreen: true,
                                        items: [
                                            {
                                                dataField: 'name',
                                                editorType: 'dxTextBox',
                                                editorOptions: nameEditorOptions,
                                                label: t('role_name'),
                                                isRequired: true,
                                                requiredMessage: 'Name is required',
                                                asyncValidation: {
                                                    validationCallback: asyncRoleValidation,
                                                    message: Required(t('name'))
                                                }
                                            },
                                            {
                                                dataField: 'description',
                                                editorType: 'dxTextBox',
                                                editorOptions: descriptionEditorOptions,
                                                label: t('role_description')
                                            },
                                            {
                                                dataField: 'applicationId',
                                                editorType: 'dxSelectBox',
                                                editorOptions: applicationEditorOptions,
                                                label: t('application'),
                                                isRequired: true,
                                                requiredMessage: Required(t('application'))
                                            },
                                            {
                                                dataField: 'accountId',
                                                editorType: 'dxSelectBox',
                                                editorOptions: accountEditorOptions,
                                                label: t('account')
                                            }, {
                                                dataField: 'parameterAccountTypeId',
                                                editorType: 'dxSelectBox',
                                                editorOptions: accountTypeEditorOptions,
                                                label: t('account_type')
                                            },
                                            {
                                                dataField: 'isRestricted',
                                                editorType: 'dxSwitch',
                                                editorOptionseditorOptions: isRestrictedEditorOptions,
                                                label: t('admin_restriction')
                                            }
                                        ]
                                    }
                                ]}
                            />
                            <div className='row justify-content-end'>
                                <div className='col-auto'>
                                    <DXButton
                                        text={t("next")}
                                        type='normal'
                                        width={120}
                                        onClick={() => {
                                            if (formRef.current) {
                                                const validationResult = formRef.current.instance.validate();
                                                if (validationResult.isValid) {
                                                    setTab('Permission');
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={clsx('tab-pane', { active: tab === 'Feature' })}>

                            <div style={{ padding: 15, fontFamily: 'Inter, Helvetica, "sans-serif"' }}>
                                {modules.map((module: any) => {
                                    const keys = getPermissionKeys(module);
                                    const defaultValues = module.features.flatMap((f: any) => f.permissions.map((p: any) => p.value || ''));

                                    const allTrue = keys.every((k: string) => selected[k] != undefined);
                                    const allFalse = keys.every((k: string) => selected[k] === undefined);
                                    const checkState = allTrue ? true : allFalse ? false : null;
                                    const isModuleExpanded = moduleExpandedStates[module.id] !== false;

                                    return (
                                        <div key={module.id} style={{ border: '1px solid #ccc', borderRadius: 8, padding: 16, marginBottom: 24, background: '#fafafa', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                                <label style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}>
                                                    <CheckBox
                                                        value={checkState}
                                                        onValueChanged={(e: any) => {
                                                            if (e.value != null) toggleGroup(keys, e.value, defaultValues);
                                                        }}
                                                    />
                                                    <span onClick={() => toggleModule(module.id)} style={{ marginLeft: 8, fontSize: 15 }}>{module.text}</span>
                                                </label>
                                                <button
                                                    onClick={() => toggleModule(module.id)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#007bff' }}
                                                >
                                                    {isModuleExpanded ? `${t("hide")} ▲` : `${t("show")} ▼`}
                                                </button>
                                            </div>

                                            {isModuleExpanded && module.features.sort((a: any, b: any) => a.text.localeCompare(b.text)).map((feature: any) => {
                                                const featureKeys = feature.permissions.map((p: any) => `${feature.id}::${p.id}::${p.dependencyType}::${p.type}`);
                                                const allTrue = featureKeys.every((k: string) => selected[k] != undefined);
                                                const allFalse = featureKeys.every((k: string) => selected[k] === undefined);
                                                const checkState = allTrue ? true : allFalse ? false : null;
                                                const isFeatureExpanded = featureExpandedStates[feature.id] === true;

                                                return (
                                                    <div key={feature.id} style={{ border: '1px dashed #ddd', borderRadius: 6, padding: 12, marginBottom: 16, marginLeft: 16, background: '#fff' }}>

                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12  }}>
                                                            <label style={{ display: 'flex', alignItems: 'center', marginBottom: 0}}>
                                                                <CheckBox
                                                                    value={checkState}
                                                                    onValueChanged={(e: any) => {
                                                                        if (e.value != null)
                                                                            toggleGroup(
                                                                                featureKeys,
                                                                                e.value,
                                                                                feature.permissions.map((p: any) => p.value || '')
                                                                            )
                                                                    }}
                                                                />
                                                                <span onClick={() => toggleFeature(feature.id)} style={{ marginLeft: 8, fontWeight: 500 }}>{feature.text}</span>
                                                            </label>
                                                            <button
                                                                onClick={() => toggleFeature(feature.id)}
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#007bff' }}
                                                            >
                                                                {isFeatureExpanded ? `${t("hide")} ▲` : `${t("show")} ▼`}
                                                            </button>
                                                        </div>



                                                        {isFeatureExpanded && (
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginLeft: 8 }}>
                                                                <thead>
                                                                    <tr style={{ background: '#f0f0f0' }}>
                                                                        <th style={{ textAlign: 'left', padding: '4px 8px', width: '30%' }}>İzin</th>
                                                                        <th style={{ textAlign: 'left', padding: '4px 8px' }}>Detay</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {feature.permissions
                                                                        .sort((a: any, b: any) => ["Required", "Supporting", "Optional"].indexOf(a.dependencyType) - ["Required", "Supporting", "Optional"].indexOf(b.dependencyType))
                                                                        .map((p: any) => {
                                                                            const key = `${feature.id}::${p.id}::${p.dependencyType}::${p.type}`;
                                                                            const isSelected = selected[key] !== undefined;
                                                                            const value = selected[key];
                                                                            return (
                                                                                <tr key={key} style={{ borderTop: '1px solid #eee' }}>
                                                                                    <td style={{ padding: '6px 8px' }}>
                                                                                        <label style={{ display: 'flex', alignItems: 'center' }}>
                                                                                            <CheckBox
                                                                                                disabled={p.dependencyType !== 'Optional'}
                                                                                                value={isSelected ? true : isSelected}
                                                                                                onValueChanged={(e: any) => {
                                                                                                    if (e.value != null) {
                                                                                                        const defaultVal = p.value || (() => {
                                                                                                            const rp = role.permissions.find((rp: any) => rp.id === p.id && rp.applicationFeatureId === feature.id && rp.valueType === p.type);
                                                                                                            return rp ? JSON.parse(rp.permissionValue).value : p.value;
                                                                                                        })();
                                                                                                        togglePermission(feature.id, p.id, p.dependencyType, p.type, e.value, defaultVal);
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                            <span style={{ marginLeft: 6 }}>{p.text}</span>
                                                                                        </label>
                                                                                    </td>
                                                                                    <td style={{ padding: '6px 8px' }}>
                                                                                        {isSelected && (
                                                                                            p.type === 'Actions' ? (
                                                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                                                                    {actionOptions.filter(item => permissions.get(key).includes(item.id)).map(action => (
                                                                                                        <label key={action.id} style={{ display: 'flex', alignItems: 'center' }}>
                                                                                                            <CheckBox
                                                                                                                disabled={p.dependencyType !== 'Optional'}
                                                                                                                value={(value && value.includes(action.id))}
                                                                                                                onValueChanged={(e) => {
                                                                                                                    const updated = e.value
                                                                                                                        ? [...value, action.id]
                                                                                                                        : value.filter((v: any) => v !== action.id);
                                                                                                                    updateValue(feature.id, p.id, p.dependencyType, p.type, updated);
                                                                                                                }}
                                                                                                            />
                                                                                                            <span style={{ marginLeft: 4 }}>{action.name}</span>
                                                                                                        </label>
                                                                                                    ))}
                                                                                                </div>
                                                                                            ) : (
                                                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                                                                    <TextBox
                                                                                                        value={value}
                                                                                                        width={500}
                                                                                                        onValueChanged={(e) => {
                                                                                                            updateValue(feature.id, p.id, p.dependencyType, p.type, e.value ?? '');
                                                                                                        }}
                                                                                                        style={{ marginLeft: 4 }}
                                                                                                    />
                                                                                                </div>
                                                                                            )
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>


                        </div>


                    </div>
                </div>

            </div>
        </>

    );
}

export default RoleDefinition;
